import { ReactElement } from "react"
import styled from "@emotion/styled"
import { BsInstagram } from "react-icons/bs"
import { useTranslation } from "../TranslationProvider"
import Button from "./Button"

const INSTAGRAM_BASE_URL = "https://www.instagram.com/reel/"
const INSTAGRAM_BASE_LOCATION = "/instagram/instagram-post-"

const INSTAGRAM_CONFIG = {
  profileUrl: "https://www.instagram.com/stepthegap/?hl=en",

  posts: [
    {
      id: 1,
      imageUrl: `${INSTAGRAM_BASE_LOCATION}1.jpg`,
      postUrl: `${INSTAGRAM_BASE_URL}DA5XF1xI181`,
      alt: "Step the Gap live performance",
    },
    {
      id: 2,
      imageUrl: `${INSTAGRAM_BASE_LOCATION}2.jpg`,
      postUrl: `${INSTAGRAM_BASE_URL}CxXvQf7t3w7`,
      alt: "Step the Gap drummer",
    },
    {
      id: 3,
      imageUrl: `${INSTAGRAM_BASE_LOCATION}3.jpg`,
      postUrl: `${INSTAGRAM_BASE_URL}ChcESe_IOhJ`,
      alt: "Step the Gap band members",
    },
    {
      id: 4,
      imageUrl: `${INSTAGRAM_BASE_LOCATION}4.jpg`,
      postUrl: `${INSTAGRAM_BASE_URL}C-rvElhoBls`,
      alt: "Step the Gap audience dancing",
    },
    {
      id: 5,
      imageUrl: `${INSTAGRAM_BASE_LOCATION}5.jpg`,
      postUrl: `${INSTAGRAM_BASE_URL}DI0tKAgoV9s`,
      alt: "Step the Gap on stage",
    },
    {
      id: 6,
      imageUrl: `${INSTAGRAM_BASE_LOCATION}6.jpg`,
      postUrl: `${INSTAGRAM_BASE_URL}C_NyzQ4o7pw`,
      alt: "Step the Gap welcome",
    },
  ],

  // Layout configuration
  postsToShow: 6, // Change to 3 for single row, 6 for double row
  columns: 3,
}

export default function InstagramGallery(): ReactElement {
  const { t } = useTranslation()

  const handlePostClick = (postUrl: string) => {
    window.open(postUrl, "_blank", "noopener,noreferrer")
  }

  const handleFollowClick = () => {
    window.open(INSTAGRAM_CONFIG.profileUrl, "_blank", "noopener,noreferrer")
  }

  const postsToDisplay = INSTAGRAM_CONFIG.posts.slice(
    0,
    INSTAGRAM_CONFIG.postsToShow
  )

  return (
    <InstagramWrapper>
      <PostsGrid columns={INSTAGRAM_CONFIG.columns}>
        {postsToDisplay.map((post) => (
          <PostContainer
            key={post.id}
            onClick={() => handlePostClick(post.postUrl)}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                handlePostClick(post.postUrl)
              }
            }}>
            <PostImage src={post.imageUrl} alt={post.alt} loading="lazy" />

            <PostOverlay className="post-overlay">
              <BsInstagram size={32} color="white" />
            </PostOverlay>
          </PostContainer>
        ))}
      </PostsGrid>

      <Button
        onClick={handleFollowClick}
        text={t("instagram.followUs")}
        icon={<BsInstagram size={20} />}
        variant="rounded"
      />
    </InstagramWrapper>
  )
}

// Styled components with your custom colors and styling
const InstagramWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: 100%;
  max-width: 990px;
  margin: 0 auto;
`

const PostsGrid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  gap: 1.25rem;
  width: 100%;

  @media (max-width: 768px) {
    gap: 0.85rem;
  }

  @media (max-width: 480px) {
    gap: 0.55rem;
    grid-template-columns: repeat(2, 1fr);
  }
`

const PostContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  cursor: pointer;
  border-radius: 1rem;
  overflow: hidden;
  border: 4px solid var(--white);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1),
    0 0 0 2px var(--special-transparent);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--primary);

  &:hover {
    transform: translateY(-4px) scale(1.02);
  }

  &:hover .post-overlay {
    opacity: 0.6;
    backdrop-filter: blur(2px);
  }

  &:hover img {
    transform: scale(1.1);
  }

  &:hover .instagram-icon {
    transform: scale(1);
  }

  &:focus {
    outline: 3px solid var(--special);
    outline-offset: 4px;
  }

  &:active {
    transform: translateY(-4px) scale(1.01);
  }
`

const PostImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, var(--primary), var(--secondary));
`

const PostOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    var(--tertiary) 0%,
    var(--secondary) 50%,
    var(--tertiary) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
`
